import { useEffect, useState } from "react";
import { api, currency } from "../lib/api";
import NestPie from "../components/NestChart";
import { Plus, Trash2, Check, X, Pencil } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  "Subscription",
  "Utility",
  "Transport",
  "Lifestyle",
  "Personal",
  "Savings",
  "Other",
];

const emptyForm = { name: "", amount: "", category: "Other", is_savings: false };

export default function Expenses() {
  const [items, setItems] = useState([]);
  const [income, setIncome] = useState({
    monthly_after_tax: 0,
    yearly_salary: 0,
    yearly_after_tax: 0,
  });
  const [editingIncome, setEditingIncome] = useState(false);
  const [incomeDraft, setIncomeDraft] = useState(income);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);

  const load = () => {
    api.get("/expenses").then((r) => setItems(r.data));
    api.get("/income").then((r) => {
      setIncome(r.data);
      setIncomeDraft(r.data);
    });
  };

  useEffect(() => {
    load();
  }, []);

  const total = items.reduce((s, e) => s + e.amount, 0);
  const savings = items
    .filter((e) => e.is_savings)
    .reduce((s, e) => s + e.amount, 0);
  const spending = total - savings;
  const leftToSpend = income.monthly_after_tax - total;

  const chartData = Object.values(
    items.reduce((acc, e) => {
      acc[e.category] = acc[e.category] || { name: e.category, value: 0 };
      acc[e.category].value += e.amount;
      return acc;
    }, {})
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.amount) return;
    const payload = {
      name: form.name.trim(),
      amount: parseFloat(form.amount),
      category: form.category,
      is_savings: form.category === "Savings" || form.is_savings,
    };
    try {
      if (editId) {
        await api.put(`/expenses/${editId}`, payload);
        toast.success("Updated");
      } else {
        await api.post("/expenses", payload);
        toast.success("Added");
      }
      setForm(emptyForm);
      setEditId(null);
      load();
    } catch {
      toast.error("Failed to save");
    }
  };

  const startEdit = (row) => {
    setEditId(row.id);
    setForm({
      name: row.name,
      amount: String(row.amount),
      category: row.category,
      is_savings: row.is_savings,
    });
  };

  const remove = async (id) => {
    await api.delete(`/expenses/${id}`);
    toast.success("Deleted");
    load();
  };

  const saveIncome = async () => {
    await api.put("/income", {
      monthly_after_tax: parseFloat(incomeDraft.monthly_after_tax) || 0,
      yearly_salary: parseFloat(incomeDraft.yearly_salary) || 0,
      yearly_after_tax: parseFloat(incomeDraft.yearly_after_tax) || 0,
    });
    setEditingIncome(false);
    load();
    toast.success("Income updated");
  };

  return (
    <div className="space-y-8" data-testid="expenses-page">
      <header>
        <div className="text-xs uppercase tracking-widest text-nest-muted">
          Monthly
        </div>
        <h1 className="font-heading text-3xl md:text-5xl font-medium mt-2">
          Income & Expenses
        </h1>
      </header>

      {/* Income + Left to spend row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="nest-card p-6" data-testid="income-card">
          <div className="flex items-center justify-between mb-4">
            <div className="text-xs uppercase tracking-widest text-nest-muted">
              Income
            </div>
            {!editingIncome ? (
              <button
                data-testid="edit-income-btn"
                onClick={() => setEditingIncome(true)}
                className="text-nest-muted hover:text-nest-brand"
              >
                <Pencil size={14} strokeWidth={1.5} />
              </button>
            ) : (
              <div className="flex gap-1">
                <button
                  onClick={saveIncome}
                  className="text-nest-brand hover:text-nest-brand-deep"
                  data-testid="save-income-btn"
                >
                  <Check size={16} strokeWidth={1.5} />
                </button>
                <button
                  onClick={() => {
                    setIncomeDraft(income);
                    setEditingIncome(false);
                  }}
                  className="text-nest-faint hover:text-nest-soft"
                >
                  <X size={16} strokeWidth={1.5} />
                </button>
              </div>
            )}
          </div>
          {!editingIncome ? (
            <>
              <div className="font-heading text-3xl tabular text-nest-good">
                {currency(income.monthly_after_tax)}
              </div>
              <div className="text-xs text-nest-muted mt-1">per month, after tax</div>
              <div className="mt-4 text-xs text-nest-muted space-y-1">
                <div>
                  Yearly salary:{" "}
                  <span className="tabular">{currency(income.yearly_salary)}</span>
                </div>
                <div>
                  Yearly after tax:{" "}
                  <span className="tabular">{currency(income.yearly_after_tax)}</span>
                </div>
              </div>
            </>
          ) : (
            <div className="space-y-3">
              <div>
                <label className="text-xs text-nest-muted block mb-1">
                  Monthly (after tax)
                </label>
                <input
                  className="nest-input w-full"
                  value={incomeDraft.monthly_after_tax}
                  onChange={(e) =>
                    setIncomeDraft({ ...incomeDraft, monthly_after_tax: e.target.value })
                  }
                  data-testid="income-monthly-input"
                />
              </div>
              <div>
                <label className="text-xs text-nest-muted block mb-1">
                  Yearly salary
                </label>
                <input
                  className="nest-input w-full"
                  value={incomeDraft.yearly_salary || ""}
                  onChange={(e) =>
                    setIncomeDraft({ ...incomeDraft, yearly_salary: e.target.value })
                  }
                />
              </div>
              <div>
                <label className="text-xs text-nest-muted block mb-1">
                  Yearly after tax
                </label>
                <input
                  className="nest-input w-full"
                  value={incomeDraft.yearly_after_tax || ""}
                  onChange={(e) =>
                    setIncomeDraft({ ...incomeDraft, yearly_after_tax: e.target.value })
                  }
                />
              </div>
            </div>
          )}
        </div>

        <div className="nest-card p-6" data-testid="totals-card">
          <div className="text-xs uppercase tracking-widest text-nest-muted mb-4">
            Outgoings
          </div>
          <div className="font-heading text-3xl tabular">{currency(total)}</div>
          <div className="text-xs text-nest-muted mt-1">
            {currency(spending)} spend · {currency(savings)} saved
          </div>
        </div>

        <div className="nest-card p-6" data-testid="left-card">
          <div className="text-xs uppercase tracking-widest text-nest-muted mb-4">
            Left to spend
          </div>
          <div
            className={`font-heading text-3xl tabular ${
              leftToSpend >= 0 ? "text-nest-good" : "text-nest-warn"
            }`}
          >
            {currency(leftToSpend)}
          </div>
          <div className="text-xs text-nest-muted mt-1">
            after {items.length} tracked outgoings
          </div>
        </div>
      </div>

      {/* Chart + Table */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="nest-card p-6 lg:col-span-2">
          <div className="text-xs uppercase tracking-widest text-nest-muted mb-4">
            Breakdown
          </div>
          <NestPie data={chartData} testId="expenses-chart" height={320} />
        </div>

        <div className="nest-card p-0 lg:col-span-3 overflow-hidden">
          <form
            onSubmit={submit}
            className="p-5 border-b border-nest-line grid grid-cols-12 gap-2 items-end"
            data-testid="expense-form"
          >
            <div className="col-span-12 sm:col-span-4">
              <label className="text-[10px] uppercase tracking-widest text-nest-muted block mb-1">
                Name
              </label>
              <input
                data-testid="expense-name-input"
                className="nest-input w-full text-sm"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Spotify"
              />
            </div>
            <div className="col-span-6 sm:col-span-3">
              <label className="text-[10px] uppercase tracking-widest text-nest-muted block mb-1">
                Amount (£)
              </label>
              <input
                data-testid="expense-amount-input"
                type="number"
                step="0.01"
                className="nest-input w-full text-sm tabular"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
              />
            </div>
            <div className="col-span-6 sm:col-span-3">
              <label className="text-[10px] uppercase tracking-widest text-nest-muted block mb-1">
                Category
              </label>
              <select
                data-testid="expense-category-select"
                className="nest-input w-full text-sm"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-span-12 sm:col-span-2 flex gap-1">
              <button
                data-testid="expense-submit-btn"
                type="submit"
                className="nest-btn-primary text-sm w-full flex items-center justify-center gap-1"
              >
                {editId ? <Check size={14} /> : <Plus size={14} />}
                {editId ? "Save" : "Add"}
              </button>
              {editId && (
                <button
                  type="button"
                  onClick={() => {
                    setEditId(null);
                    setForm(emptyForm);
                  }}
                  className="text-nest-muted hover:text-nest-ink"
                >
                  <X size={16} />
                </button>
              )}
            </div>
          </form>

          <div className="max-h-[420px] overflow-y-auto" data-testid="expense-list">
            {items.length === 0 ? (
              <div className="p-8 text-center text-nest-faint text-sm">
                No expenses yet
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-[10px] uppercase tracking-widest text-nest-muted sticky top-0 bg-nest-surface-4 backdrop-blur z-10">
                  <tr>
                    <th className="text-left px-5 py-3 font-medium">Name</th>
                    <th className="hidden sm:table-cell text-left px-2 py-3 font-medium">Category</th>
                    <th className="text-right px-2 py-3 font-medium">Amount</th>
                    <th className="w-20"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((row) => (
                    <tr
                      key={row.id}
                      className="border-t border-nest-line hover:bg-nest-surface-1"
                      data-testid={`expense-row-${row.id}`}
                    >
                      <td className="pl-4 pr-2 sm:px-5 py-3">
                        {row.name}
                        {row.is_savings && (
                          <span className="ml-2 text-[9px] uppercase tracking-widest text-nest-brand bg-nest-brand/10 px-1.5 py-0.5 rounded">
                            savings
                          </span>
                        )}
                        <div className="sm:hidden text-xs text-nest-muted mt-0.5">{row.category}</div>
                      </td>
                      <td className="hidden sm:table-cell px-2 py-3 text-nest-muted">{row.category}</td>
                      <td className="px-2 py-3 text-right tabular">
                        {currency(row.amount)}
                      </td>
                      <td className="px-3 py-3 text-right">
                        <div className="inline-flex gap-2">
                          <button
                            onClick={() => startEdit(row)}
                            className="text-nest-faint hover:text-nest-brand"
                            data-testid={`edit-expense-${row.id}`}
                          >
                            <Pencil size={14} strokeWidth={1.5} />
                          </button>
                          <button
                            onClick={() => remove(row.id)}
                            className="text-nest-faint hover:text-nest-warn"
                            data-testid={`delete-expense-${row.id}`}
                          >
                            <Trash2 size={14} strokeWidth={1.5} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
